import * as React from "react";
import apiClient from "@/api-client";
import { CircularProgress, IconButton, Tooltip } from "@mui/material";
import { PauseIcon } from "@phosphor-icons/react/dist/ssr/Pause";
import { PlayIcon } from "@phosphor-icons/react/dist/ssr/Play";

import { logger } from "@/lib/default-logger";
import { voiceSamplePath } from "@/hooks/api/use-voices";

/**
 * Playing one pre-recorded voice sample.
 *
 * A real `<audio>` element against the API's inline sample route, but not pointed straight at it:
 * the route is behind a session and this client authenticates with a bearer header, which an
 * `<audio src>` cannot carry. So the bytes are fetched through the same axios instance as
 * everything else and handed to the element as an object URL — the same shape the call-recording
 * player already uses.
 *
 * Only one sample plays at a time, across every screen. The currently-playing element is held in
 * a module-level slot rather than in React state on purpose: two lists of voices on the same
 * screen are different trees, and "stop the other one" is not a question either of them can
 * answer on its own. Each element's own `pause` event puts its button back, so a button
 * that was silenced by another one is never left saying it is playing.
 */
let playing: HTMLAudioElement | null = null;

function claimPlayback(element: HTMLAudioElement): void {
	if (playing && playing !== element) {
		playing.pause();
		playing.currentTime = 0;
	}

	playing = element;
}

function releasePlayback(element: HTMLAudioElement): void {
	if (playing === element) playing = null;
}

type SampleStatus = "idle" | "loading" | "playing" | "error";

export interface VoiceSampleButtonProps {
	/** `sampleUrl` exactly as the API minted it. Null where no sample has been recorded yet. */
	sampleUrl: string | null;
	/** Whose sample this is. Used for the tooltip and the accessible name, never rendered as text. */
	name: string;
	size?: "small" | "medium";
	disabled?: boolean;
}

export function VoiceSampleButton({
	sampleUrl,
	name,
	size = "small",
	disabled = false,
}: VoiceSampleButtonProps): React.JSX.Element {
	const audioRef = React.useRef<HTMLAudioElement | null>(null);
	const objectUrlRef = React.useRef<string | null>(null);
	/** The fetch in flight, so it can be abandoned if the button goes away before it answers. */
	const fetchRef = React.useRef<AbortController | null>(null);
	const [status, setStatus] = React.useState<SampleStatus>("idle");

	/**
	 * Drop the fetched bytes when the sample behind this button changes, and on the way out.
	 *
	 * An object URL holds its blob alive until it is revoked, and a sample is up to 5 MiB — a
	 * curation screen where every replaced sample leaked one would grow for as long as it is open.
	 */
	React.useEffect(() => {
		// Read now rather than in the cleanup: by then React may have detached the element, and the
		// one thing the cleanup has to do is stop the sound this component is making.
		const audio = audioRef.current;

		return () => {
			// A row remounts this button on any change to the voice — a toggled switch is enough.
			// Without this the abandoned instance's fetch still resolves, plays a detached element
			// nobody has a button for, and leaks the object URL it made on the way.
			fetchRef.current?.abort();
			fetchRef.current = null;

			if (audio) {
				audio.pause();
				releasePlayback(audio);
			}

			if (objectUrlRef.current) {
				globalThis.URL.revokeObjectURL(objectUrlRef.current);
				objectUrlRef.current = null;
			}
		};
	}, [sampleUrl]);

	const handleToggle = async (): Promise<void> => {
		const audio = audioRef.current;
		if (!audio || !sampleUrl) return;

		if (status === "playing") {
			audio.pause();
			return;
		}

		try {
			if (!objectUrlRef.current) {
				setStatus("loading");

				// The response carries the content type the API sniffed from the bytes at upload, so
				// the blob is already typed correctly and must not be relabelled here.
				const controller = new AbortController();
				fetchRef.current = controller;

				const response = await apiClient.get(voiceSamplePath(sampleUrl), {
					responseType: "blob",
					signal: controller.signal,
				});

				if (controller.signal.aborted) return;
				fetchRef.current = null;

				objectUrlRef.current = globalThis.URL.createObjectURL(response.data as Blob);
				audio.src = objectUrlRef.current;
			}

			claimPlayback(audio);
			await audio.play();
		} catch (error) {
			// An abandoned fetch is this component going away, not a sample that cannot be played.
			if (fetchRef.current?.signal.aborted) return;

			logger.error(error);
			setStatus("error");
		}
	};

	const label = sampleUrl ? `Play the ${name} sample` : "No sample recorded yet";
	// What pressing it does now, which is the opposite of what it is doing.
	const action = status === "playing" ? `Pause the ${name} sample` : label;

	return (
		<React.Fragment>
			{/* preload="none": nothing is fetched until somebody asks to hear it. */}
			<audio
				ref={audioRef}
				preload="none"
				onPlay={() => setStatus("playing")}
				onPause={() => setStatus("idle")}
				onEnded={() => setStatus("idle")}
			/>

			<Tooltip title={status === "error" ? `The ${name} sample could not be played` : action}>
				{/* A disabled button dispatches no events, so the tooltip needs a wrapper to hang off. */}
				<span>
					<IconButton
						aria-label={action}
						color={status === "error" ? "error" : "primary"}
						size={size}
						disabled={disabled || !sampleUrl || status === "loading"}
						onClick={() => void handleToggle()}
					>
						{status === "loading" ? (
							<CircularProgress size={16} />
						) : status === "playing" ? (
							<PauseIcon fontSize="var(--icon-fontSize-md)" weight="fill" />
						) : (
							<PlayIcon fontSize="var(--icon-fontSize-md)" weight="fill" />
						)}
					</IconButton>
				</span>
			</Tooltip>
		</React.Fragment>
	);
}
